import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Depoimentos.css';

// Import thumbnail images
import thumbnail1 from '../../../../assets/images/depoimento-1.jpg';
import thumbnail2 from '../../../../assets/images/depoimento-2.jpg';
import thumbnail3 from '../../../../assets/images/depoimento-3.jpg';
import thumbnail4 from '../../../../assets/images/depoimento-4.jpg';
import thumbnail5 from '../../../../assets/images/depoimento-5.jpg';
import thumbnail6 from '../../../../assets/images/depoimento-6.jpg';
const Depoimentos = () => {
  // Video data using YouTube links with matching thumbnails
  const videos = [
    {
      id: 1,
      youtubeId: '7qzuzEhtDv8',
      thumbnail: thumbnail1
    },
    {
      id: 2,
      youtubeId: 'mFYkNJzubF8',
      thumbnail: thumbnail2
    },
    {
      id: 3,
      youtubeId: 'E_Lw47PcQCE',
      thumbnail: thumbnail3
    },
    {
      id: 4,
      youtubeId: 'Y5MBepZ9F40',
      thumbnail: thumbnail4
    },
    {
      id: 5,
      youtubeId: '6NoLxAXI5Sw',
      thumbnail: thumbnail5
    },
    {
      id: 6,
      youtubeId: 'lzq2USCH90I',
      thumbnail: thumbnail6
    }
  ];

  const [isHovering, setIsHovering] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const intervalRef = useRef(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Use useCallback to memoize the handleAutoScroll function
  const handleAutoScroll = useCallback(() => {
    if (!isAnimating) {
      setCurrentIndex(prevIndex => (prevIndex + 0.01) % videos.length);
    }
  }, [videos.length, isAnimating]);

  // Function to scroll the carousel
  const scroll = (direction) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    // Calculate the target index
    const step = direction === 'left' ? -1 : 1;
    
    // Use CSS transitions instead of JavaScript animation
    // First, update the currentIndex immediately
    setCurrentIndex(prevIndex => {
      const newIndex = (prevIndex + step + videos.length) % videos.length;
      return newIndex;
    });
    
    // After the transition duration, clear the animation flag
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
    
    // Mark that user has interacted with carousel
    setUserInteracted(true);
  };

  // Function to play a video
  const playVideo = (videoId) => {
    if (playingVideo === videoId) {
      setPlayingVideo(null);
    } else {
      setPlayingVideo(videoId);
      // Mark that user has interacted with carousel
      setUserInteracted(true);
    }
  };

  // Set up auto-scroll when component mounts
  useEffect(() => {
    // Only start auto-scroll if user hasn't interacted and no video is playing
    if (!userInteracted && !isHovering && !playingVideo) {
      intervalRef.current = setInterval(handleAutoScroll, 30);
    } else {
      // Clear interval if user has interacted, is hovering, or a video is playing
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isHovering, playingVideo, userInteracted, handleAutoScroll]);

  // Generate the visible videos based on the current index
  const getVisibleVideos = () => {
    const result = [];
    
    // We'll show 7 videos in the carousel - 3 before, current, and 3 after
    for (let i = -3; i <= 3; i++) {
      let index = Math.floor(currentIndex) + i;
      
      // Handle wrapping for infinite effect
      while (index < 0) index += videos.length;
      index = index % videos.length;
      
      result.push({
        ...videos[index],
        position: i
      });
    }
    
    return result;
  };

  const visibleVideos = getVisibleVideos();

  return (
    <section 
      id="depoimentos" 
      className="depoimentos-section"
    >
      <div className="container">
        <div className="section-header">
          <h2>Depoimentos</h2>
          <p>Ouça o que outros pais têm a dizer sobre nossa escola</p>
        </div>

        <div className="netflix-carousel-container">
          <button 
            className="netflix-carousel-arrow left"
            onClick={() => scroll('left')}
            disabled={isAnimating}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          
          <div 
            className="netflix-carousel-track"
            onMouseEnter={() => {
              setIsHovering(true);
              setUserInteracted(true); // Mark as interacted on hover
            }}
            onMouseLeave={() => setIsHovering(false)}
          >
            {visibleVideos.map((video) => (
              <div 
                key={`${video.id}-${video.position}`} 
                className={`netflix-video-card position-${video.position}`}
                onClick={() => playVideo(video.id)}
                style={{
                  transform: `translateX(${video.position * 220}px)`,
                  opacity: Math.abs(video.position) > 2 ? 0.5 : 1,
                  '--position': video.position
                }}
              >
                {playingVideo === video.id ? (
                  <div className="netflix-video-player-container">
                    <iframe
                      className="netflix-video-player"
                      src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1`}
                      title={`Depoimento ${video.id}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                ) : (
                  <div className="netflix-thumbnail-container">
                    <img 
                      src={video.thumbnail} 
                      alt={`Depoimento ${video.id}`} 
                      className="video-thumbnail" 
                    />
                    <div className="netflix-play-button">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <button 
            className="netflix-carousel-arrow right"
            onClick={() => scroll('right')}
            disabled={isAnimating}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Depoimentos; 